import type { EmailParticipant } from '../../api/comms';

/**
 * Relative time formatting: "2m ago", "3h ago", "Yesterday", "Jan 15"
 */
export function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;

  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * State-aware "Started ..." label for the WorkPlan/WorkStream cycle rail.
 *
 * The prototype distinguishes a RUNNING cycle ("Started 2m ago" — live,
 * relative) from a COMPLETED one ("Started from Jun 28" — settled,
 * absolute). `RecentRunPanel` and `ExecutionResult` both render this label
 * so they stay in lock-step with the prototype instead of each inlining a
 * state-unaware `formatRelativeTime` call.
 *
 * - running:   `Started 2m ago` / `Started just now` (relative, live)
 * - completed:  `Started from Jun 28` (absolute short date)
 * - missing/invalid timestamp: `Started just now` (safe fallback)
 */
export function formatStartedFrom(
  startedAtIso: string | null | undefined,
  state: 'running' | 'completed',
): string {
  if (!startedAtIso) return 'Started just now';
  const then = new Date(startedAtIso).getTime();
  if (Number.isNaN(then)) return 'Started just now';
  if (state === 'running') {
    return `Started ${formatRelativeTime(startedAtIso)}`;
  }
  return `Started from ${new Date(startedAtIso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })}`;
}

/**
 * Extract external (non-user) email addresses from thread participants
 */
export function extractExternalEmails(
  participants: EmailParticipant[],
  userEmail: string | undefined,
): string[] {
  if (!userEmail) return participants.map((p) => p.email).filter(Boolean);
  const lower = userEmail.toLowerCase();
  return participants
    .map((p) => p.email)
    .filter((e) => e && e.toLowerCase() !== lower);
}

/**
 * Get name for display from participant
 */
export function getParticipantName(participant: EmailParticipant): string {
  return participant.name || participant.email || 'Unknown';
}

/**
 * Get initials from name or email
 */
export function getInitials(name?: string, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0][0]?.toUpperCase() || '?';
  }
  if (email) return email[0]?.toUpperCase() || '?';
  return '?';
}

/**
 * Deterministic avatar color based on string hash
 */
const AVATAR_COLORS = [
  'bg-blue-500/20 text-blue-400',
  'bg-emerald-500/20 text-emerald-400',
  'bg-purple-500/20 text-purple-400',
  'bg-amber-500/20 text-amber-400',
  'bg-rose-500/20 text-rose-400',
  'bg-cyan-500/20 text-cyan-400',
  'bg-orange-500/20 text-orange-400',
  'bg-teal-500/20 text-teal-400',
  'bg-indigo-500/20 text-indigo-400',
  'bg-pink-500/20 text-pink-400',
];

export function avatarColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/**
 * Format monetary amount: $1.2M, $45K, $500
 */
export function formatAmount(n: number | undefined | null): string {
  if (n == null) return '--';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n.toLocaleString()}`;
}

/**
 * Format monetary amount with full precision: $330,000
 */
export function formatAmountFull(n: number | undefined | null): string {
  if (n == null) return '--';
  return '$' + Math.round(n).toLocaleString('en-US');
}

/** System/no-reply addresses that should not be auto-created as contacts */
const SYSTEM_EMAIL_PATTERNS = [
  'noreply@', 'no-reply@', 'mailer-daemon@', 'postmaster@',
  'notifications@', 'notification@', 'bounce@', 'donotreply@',
  'do-not-reply@', 'auto-', 'system@', 'admin@',
];

export function isSystemEmail(email: string): boolean {
  const lower = email.toLowerCase();
  return SYSTEM_EMAIL_PATTERNS.some((p) => lower.startsWith(p) || lower.includes(`+${p}`));
}

/**
 * Gravatar URL from email. Uses MD5 hash.
 * Falls back to 404 so the <img> onError can show initials.
 */
export function gravatarUrl(email: string, size = 80): string {
  const hash = md5(email.trim().toLowerCase());
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=404`;
}

/** Simple MD5 for Gravatar (browser-safe, no dependencies) */
function md5(str: string): string {
  function cmn(q: number, a: number, b: number, x: number, s: number, t: number) {
    a = (a + q + x + t) & 0xffffffff;
    return (((a << s) | (a >>> (32 - s))) + b) & 0xffffffff;
  }
  function ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number) { return cmn((b & c) | (~b & d), a, b, x, s, t); }
  function gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number) { return cmn((b & d) | (c & ~d), a, b, x, s, t); }
  function hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number) { return cmn(b ^ c ^ d, a, b, x, s, t); }
  function ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number) { return cmn(c ^ (b | ~d), a, b, x, s, t); }

  const n = str.length;
  let state = [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476];
  let i: number;
  const bytes: number[] = [];
  for (i = 0; i < n; i++) {
    const code = str.charCodeAt(i);
    if (code < 0x80) bytes.push(code);
    else if (code < 0x800) { bytes.push(0xc0 | (code >> 6)); bytes.push(0x80 | (code & 0x3f)); }
    else { bytes.push(0xe0 | (code >> 12)); bytes.push(0x80 | ((code >> 6) & 0x3f)); bytes.push(0x80 | (code & 0x3f)); }
  }
  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);
  const bitLen = n * 8;
  bytes.push(bitLen & 0xff, (bitLen >> 8) & 0xff, (bitLen >> 16) & 0xff, (bitLen >> 24) & 0xff, 0, 0, 0, 0);
  for (i = 0; i < bytes.length; i += 64) {
    const w: number[] = [];
    for (let j = 0; j < 64; j += 4) w.push(bytes[i + j] | (bytes[i + j + 1] << 8) | (bytes[i + j + 2] << 16) | (bytes[i + j + 3] << 24));
    let [a, b, c, d] = state;
    a = ff(a, b, c, d, w[0], 7, 0xd76aa478); d = ff(d, a, b, c, w[1], 12, 0xe8c7b756); c = ff(c, d, a, b, w[2], 17, 0x242070db); b = ff(b, c, d, a, w[3], 22, 0xc1bdceee);
    a = ff(a, b, c, d, w[4], 7, 0xf57c0faf); d = ff(d, a, b, c, w[5], 12, 0x4787c62a); c = ff(c, d, a, b, w[6], 17, 0xa8304613); b = ff(b, c, d, a, w[7], 22, 0xfd469501);
    a = ff(a, b, c, d, w[8], 7, 0x698098d8); d = ff(d, a, b, c, w[9], 12, 0x8b44f7af); c = ff(c, d, a, b, w[10], 17, 0xffff5bb1); b = ff(b, c, d, a, w[11], 22, 0x895cd7be);
    a = ff(a, b, c, d, w[12], 7, 0x6b901122); d = ff(d, a, b, c, w[13], 12, 0xfd987193); c = ff(c, d, a, b, w[14], 17, 0xa679438e); b = ff(b, c, d, a, w[15], 22, 0x49b40821);
    a = gg(a, b, c, d, w[1], 5, 0xf61e2562); d = gg(d, a, b, c, w[6], 9, 0xc040b340); c = gg(c, d, a, b, w[11], 14, 0x265e5a51); b = gg(b, c, d, a, w[0], 20, 0xe9b6c7aa);
    a = gg(a, b, c, d, w[5], 5, 0xd62f105d); d = gg(d, a, b, c, w[10], 9, 0x02441453); c = gg(c, d, a, b, w[15], 14, 0xd8a1e681); b = gg(b, c, d, a, w[4], 20, 0xe7d3fbc8);
    a = gg(a, b, c, d, w[9], 5, 0x21e1cde6); d = gg(d, a, b, c, w[14], 9, 0xc33707d6); c = gg(c, d, a, b, w[3], 14, 0xf4d50d87); b = gg(b, c, d, a, w[8], 20, 0x455a14ed);
    a = gg(a, b, c, d, w[13], 5, 0xa9e3e905); d = gg(d, a, b, c, w[2], 9, 0xfcefa3f8); c = gg(c, d, a, b, w[7], 14, 0x676f02d9); b = gg(b, c, d, a, w[12], 20, 0x8d2a4c8a);
    a = hh(a, b, c, d, w[5], 4, 0xfffa3942); d = hh(d, a, b, c, w[8], 11, 0x8771f681); c = hh(c, d, a, b, w[11], 16, 0x6d9d6122); b = hh(b, c, d, a, w[14], 23, 0xfde5380c);
    a = hh(a, b, c, d, w[1], 4, 0xa4beea44); d = hh(d, a, b, c, w[4], 11, 0x4bdecfa9); c = hh(c, d, a, b, w[7], 16, 0xf6bb4b60); b = hh(b, c, d, a, w[10], 23, 0xbebfbc70);
    a = hh(a, b, c, d, w[13], 4, 0x289b7ec6); d = hh(d, a, b, c, w[0], 11, 0xeaa127fa); c = hh(c, d, a, b, w[3], 16, 0xd4ef3085); b = hh(b, c, d, a, w[6], 23, 0x04881d05);
    a = hh(a, b, c, d, w[9], 4, 0xd9d4d039); d = hh(d, a, b, c, w[12], 11, 0xe6db99e5); c = hh(c, d, a, b, w[15], 16, 0x1fa27cf8); b = hh(b, c, d, a, w[2], 23, 0xc4ac5665);
    a = ii(a, b, c, d, w[0], 6, 0xf4292244); d = ii(d, a, b, c, w[7], 10, 0x432aff97); c = ii(c, d, a, b, w[14], 15, 0xab9423a7); b = ii(b, c, d, a, w[5], 21, 0xfc93a039);
    a = ii(a, b, c, d, w[12], 6, 0x655b59c3); d = ii(d, a, b, c, w[3], 10, 0x8f0ccc92); c = ii(c, d, a, b, w[10], 15, 0xffeff47d); b = ii(b, c, d, a, w[1], 21, 0x85845dd1);
    a = ii(a, b, c, d, w[8], 6, 0x6fa87e4f); d = ii(d, a, b, c, w[15], 10, 0xfe2ce6e0); c = ii(c, d, a, b, w[6], 15, 0xa3014314); b = ii(b, c, d, a, w[13], 21, 0x4e0811a1);
    a = ii(a, b, c, d, w[4], 6, 0xf7537e82); d = ii(d, a, b, c, w[11], 10, 0xbd3af235); c = ii(c, d, a, b, w[2], 15, 0x2ad7d2bb); b = ii(b, c, d, a, w[9], 21, 0xeb86d391);
    state = [(state[0] + a) & 0xffffffff, (state[1] + b) & 0xffffffff, (state[2] + c) & 0xffffffff, (state[3] + d) & 0xffffffff];
  }
  return state.map(v => [v & 0xff, (v >> 8) & 0xff, (v >> 16) & 0xff, (v >> 24) & 0xff].map(b => b.toString(16).padStart(2, '0')).join('')).join('');
}

/**
 * Full date+time formatting: "Wed, Mar 8, 02:30 PM"
 */
export function formatDate(isoStr: string): string {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format snooze expiry relative to now
 */
export function formatSnoozeExpiry(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));

  if (diffHours < 1) return 'less than an hour';
  if (diffHours < 24) return `${diffHours}h from now`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) return 'tomorrow';
  if (diffDays < 7) return `${diffDays} days from now`;
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Extract domain from email address
 */
export function extractDomain(email: string): string | null {
  const at = email.lastIndexOf('@');
  if (at === -1) return null;
  const domain = email.slice(at + 1).toLowerCase();
  // Skip common free email providers
  const FREE_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com', 'protonmail.com', 'mail.com'];
  if (FREE_DOMAINS.includes(domain)) return null;
  return domain;
}

/**
 * Convert plain text email body to simple HTML paragraphs.
 * Used by EmailComposer when sending via comms API.
 */
export function textToHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return escaped
    .split('\n')
    .map(line => (line.length > 0 ? `<p>${line}</p>` : '<br>'))
    .join('');
}
